import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { SaveBar } from "../../../../components/utils/SaveBar";
import { fetchReport } from "../../../../api/service/report";
import { itemListsApi } from "../../../../api/item_lists";
import {
  FullReportTitle,
  FullReportAddress,
  ReportStatus,
  ReportAppointmentApprovedStatus,
  ReportInvoiceStatus,
  ReportMustBeRectified,
} from "../../../../components/dashboard/service/reports/utils";
import {
  CheckBadgeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import ReportItemConsumptionList from "../../../../components/dashboard/service/reports/report-item-consumption-list";
import ReportItemHandlingList from "../../../../components/dashboard/service/reports/report-item-handling-list";
import { PMNotice } from "../../../../components/utils/notifications";
import { CopyToClipboard } from "../../../../components/utils/Div";
import { mapsServiceUrl, serviceAgreementPath } from "../../../../components/Utils";

export const ReportItemConsumptionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSelector((state) => state.session.currentUser.settings);

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState([]);

  // Determine if user can edit this report
  const canEdit =
    report?.user_can_edit_with_rights !== false ||
    report?.user_can_edit !== false;

  // Load report with optimized fields
  const loadReport = async () => {
    if (!id) return;

    setIsLoading(true);
    setError(false);
    setErrors([]);

    const params = {
      fields: "id,report_number,report_type.*,status,user_can_edit_with_rights,approved,not_approved,updated_after_process,process_note,approval_status,must_be_rectified,customer.*,contact.*,project.*,delivery_address,address,agreement.*,executing_employee",
    };

    try {
      const data = await fetchReport(id, params);
      const reportData = data?.data;
      
      // Fetch item_lists separately from item_lists API
      try {
        const itemListsResponse = await itemListsApi.getReportItemLists(id);
        const itemLists = itemListsResponse?.data?.data || [];
        reportData.item_lists = itemLists;
      } catch (itemListsError) {
        console.error("Error loading item lists:", itemListsError);
        reportData.item_lists = [];
      }
      
      setReport(reportData);
      setIsLoading(false);
    } catch (error) {
      toast.error("Kunne ikke laste rapport");
      setError(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [id]);

  const handleOnRefresh = async () => {
    // Reload report with same optimized fields
    await loadReport();
  };

  // Function to reload only item_lists without reloading entire report
  const reloadItemLists = async () => {
    if (!id) return;

    try {
      // Fetch item_lists directly from item_lists API
      const response = await itemListsApi.getReportItemLists(id);
      const itemLists = response?.data?.data || [];

      // Update only item_lists in report state
      setReport(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          item_lists: itemLists || []
        };
      });
    } catch (error) {
      console.error("Error reloading item lists:", error);
      // Don't show error toast, just log it
    }
  };

  // Check for updates from handling page via localStorage
  useEffect(() => {
    const checkForUpdates = () => {
      const lastUpdate = localStorage.getItem(`item_handling_update_${id}`);
      const lastCheck = localStorage.getItem(`item_handling_check_${id}`);

      if (lastUpdate && (!lastCheck || lastUpdate > lastCheck)) {
        console.log('Item handling update detected, refreshing data...');
        localStorage.setItem(`item_handling_check_${id}`, lastUpdate);
        reloadItemLists();
      }
    };

    // Check immediately and then every 5 seconds
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 5000);

    return () => clearInterval(interval);
  }, [id]);

  if (isLoading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={errors} />;
  }

  if (!report?.id) {
    return <ResourceEmpty />;
  }

  const headerMeta = [
    {
      component: <ReportStatus report={report} />,
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      component: <ReportAppointmentApprovedStatus report={report} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["new", "ongoing"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      component: <ReportMustBeRectified rectify={report?.must_be_rectified} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["accomplished"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      label: report?.customer?.customer_number + " " + report?.customer?.name,
      description: "",
      path: "/dashboard/customers/" + report?.customer?.id,
      action: null,
      icon: UsersIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.id ? false : true,
      copy: false,
    },
    {
      label: report?.project?.displayTitle,
      description: "",
      path: "/dashboard/projects/" + report?.project?.id,
      action: null,
      icon: BuildingOfficeIcon,
      textColor: "text-teal-700",
      hidden: report?.project?.id ? false : true,
      copy: false,
    },
    {
      label: (
        <CopyToClipboard
          path={mapsServiceUrl(
            FullReportAddress(report),
            settings?.preferred_maps_service
          )}
          content={FullReportAddress(report)}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: MapPinIcon,
      textColor: "text-teal-700",
      hidden: report?.delivery_address || report.address ? false : true,
      copy: true,
    },
    {
      label: (
        <CopyToClipboard
          path={"tel:" + report?.customer?.mobile}
          content={report?.customer?.mobile}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: PhoneIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.mobile ? false : true,
      copy: true,
    },
    {
      label: "Serviceavtale " + report?.agreement?.agreement_number,
      description: "",
      path: serviceAgreementPath({ serviceAgreement: report?.agreement }),
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: report?.agreement?.id ? false : true,
      copy: true,
    },
  ];

  return (
    <>
      <DashboardHeader
        subtitle="Vareforbruk"
        title={FullReportTitle(report)}
        meta={headerMeta}
      >
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          {/* Report Info Section */}
          <div className="mb-6 bg-white shadow sm:rounded-lg">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <CubeIcon className="h-6 w-6 text-teal-600 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Vareforbruk for rapport
                  </h2>
                  <p className="text-sm text-gray-500 mt-1 truncate">
                    {report?.report_number} - {report?.report_type?.displayTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Item Consumption Content */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 sm:px-6 py-4 sm:py-6">
              {!canEdit && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Kun visning
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Vareforbruket kan ikke redigeres. Du kan se innholdet,
                          men ikke lagre endringer.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <ReportItemConsumptionList
                  disabled={!canEdit}
                  report={report}
                  onReportUpdate={reloadItemLists}
                />
                

              </div>
            </div>
          </div>
            <div className="bg-white shadow sm:rounded-lg mt-6">
                <div className="px-4 sm:px-6 py-4 sm:py-6">
                                {/* Item Handling List - shown when all items are handled */}
                                {report?.item_lists && report.item_lists.length > 0 && (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Varehåndtering</h3>
                            <button
                                onClick={reloadItemLists}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                                title="Oppdater håndteringsliste"
                            >
                                🔄 Oppdater
                            </button>
                        </div>
                        <ReportItemHandlingList
                            itemList={report.item_lists.find(list => list.list_type === 'handling')}
                            isLoading={false}
                        />
                    </div>
                    )}
                </div>
            </div>
        </div>

      </DashboardHeader>
      
      <SaveBar
        alwaysShow={true}
        onBack={() => navigate(`/dashboard/service/reports/${report?.id}`)}
        backLabel="Tilbake til rapport"
      />
    </>
  );
};

